/**
 * 邮件撰写组件
 *
 * 提供完整的邮件编写界面，包括收件人/抄送管理、富文本编辑器、附件上传、
 * 模板/片段/产品插入、签名选择、定时发送和草稿保存。
 *
 * 自定义元素：`crm-email-compose`
 *
 * 核心功能：
 * - **收件人管理**：Chip 式输入，支持搜索联想（后端模糊匹配客户邮箱），
 *   自动匹配已知客户并在 Chip 上标记为可点击（打开客户详情弹窗）
 * - **富文本编辑**：基于 `crm-rich-editor` 组件，支持签名、片段、产品名插入
 * - **模板系统**：异步搜索邮件模板并加载，支持内联图片 CID URL 解析
 * - **附件上传**：实时进度展示，完成后显示文件列表
 * - **签名管理**：自动加载账户的活跃签名，支持手动切换/移除
 * - **定时发送**：可选设置发送时间
 * - **草稿保存**：支持保存草稿供后续继续编辑
 * - **转发/回复**：支持转发头和引用内容拼接
 *
 * 对外事件：
 * - `crm-email-compose-sent`：邮件发送成功后触发
 * - `crm-email-compose-close`：用户点击取消/关闭时触发
 * - `recipients-changed`：收件人/抄送列表变化时触发（300ms 防抖）
 *
 * 依赖模块：
 * - `emailStore`：邮件全局状态（账户列表、签名）
 * - `emailSendApi`：发送/草稿 API
 * - `emailTemplateApi`：模板 API
 * - `productApi`：产品 API
 * - `crm-rich-editor`：富文本编辑器
 * - `crm-signature-selector`：签名选择器
 * - `crm-select`：下拉选择器
 * - `crm-customer-view-modal`：客户详情弹窗
 * - `email-helpers`：收件人匹配、签名处理等工具函数
 */
import { css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { emailStore } from "../store.js";
import { BaseElement } from "../base-element.js";
import { emailSendApi, emailTemplateApi, productApi, API_BASE_URL } from "../api.js";
import type { Customer, EmailAccount } from "../types.js";
import { toast } from "./toast.js";
import "./rich-editor.js";
import "./signature-selector.js";
import "./select.js";
import "./customer-view-modal.js";
import { isNetworkError, formatFileSize, cleanQuoteHtml, sanitizeEmailHtml } from "../utils/index.js";
import { editorContentWithoutSignature, wrapSignatureHTML, removeAttachmentByIndex, fetchRecipientMatches, handleRecipientChipClick, searchRecipientOptions } from "../utils/email-helpers.js";

/** 编辑器空内容时的默认占位（8 个空行，提供足够的初始编辑空间） */
const DEFAULT_EMPTY_LINES = "<div><br></div>".repeat(8);

@customElement("crm-email-compose")
export class CrmEmailCompose extends BaseElement {
  // ---- 预填属性 ----
  // 这些属性由父组件传入，用于初始化撰写表单（如快速回复、草稿编辑、转发、回复等场景）

  /** 单个收件人邮箱（用于快速回复等同步初始化场景） */
  @property({ type: String }) toEmail = "";
  /** 邮件主题（由父组件预填） */
  @property({ type: String }) subject = "";
  /** 关联的客户 ID（发送时附带，用于关联记录） */
  @property({ type: Number }) customerId = 0;
  /** 草稿 ID（编辑草稿时使用） */
  @property({ type: Number }) draftId = 0;
  /** 草稿邮件正文内容 */
  @property({ type: String }) draftContent = "";
  /** 草稿收件人列表 */
  @property({ type: Array }) draftTo: string[] = [];
  /** 草稿抄送列表 */
  @property({ type: Array }) draftCc: string[] = [];
  /** 草稿使用的发件账户 ID */
  @property({ type: Number }) draftAccountId = 0;
  /** 草稿附件列表 */
  @property({ type: Array }) draftAttachments: Array<{ id: number; filename: string; size: number }> = [];
  /** 撰写会话编号（变化时重置全部状态，用于区分不同会话：新邮件/转发/回复/草稿编辑） */
  @property({ type: Number }) composeSession = 0;
  /** 转发邮件的头部信息 HTML（如 "From: ... To: ... Subject: ..."） */
  @property({ type: String }) forwardHeader = "";
  /** 转发邮件的引用内容 HTML */
  @property({ type: String }) forwardHtml = "";
  /** 回复邮件的 Message-ID（用于 In-Reply-To 头） */
  @property({ type: String }) replyToMessageId = "";
  /** 回复邮件的 References 头（邮件引用链） */
  @property({ type: String }) replyReferences = "";
  /** 回复邮件所属的线程 ID */
  @property({ type: String }) replyThreadId = "";

  // ---- 内部状态 ----
  /** 可用发件账户列表（从 emailStore 获取） */
  @state() private accounts: EmailAccount[] = [];
  /** 当前选中的发件账户 ID（null 表示未选择） */
  @state() private selectedAccount: number | null = null;
  /** 激活签名请求序号，避免切换账户后插入旧账户签名。 */
  private _signatureRequestId = 0;
  /** 收件人邮箱列表 */
  @state() private toRecipients: string[] = [];
  /** 收件人输入框当前值 */
  @state() private toInput = "";
  /** 收件人搜索联想选项 */
  @state() private toOptions: { label: string; value: string }[] = [];
  /** 是否显示收件人下拉列表 */
  @state() private showToDropdown = false;
  /** 收件人搜索防抖计时器 */
  private toTimer: number | null = null;
  /** 是否显示抄送区域 */
  @state() private showCc = false;
  /** 抄送邮箱列表 */
  @state() private ccRecipients: string[] = [];
  /** 抄送输入框当前值 */
  @state() private ccInput = "";
  /** 抄送搜索联想选项 */
  @state() private ccOptions: { label: string; value: string }[] = [];
  /** 是否显示抄送下拉列表 */
  @state() private showCcDropdown = false;
  /** 抄送搜索防抖计时器 */
  private ccTimer: number | null = null;
  /** 收件人变化事件防抖计时器（300ms） */
  private _recipientChangeTimer: number | null = null;
  /** 正在上传中的附件列表（显示进度条） */
  @state() private uploadingAttachments: Array<{
    name: string;
    size: number;
    progress: number;
  }> = [];
  /** 已上传完成的附件列表 */
  @state() private attachedFiles: Array<{
    id: number;
    filename: string;
    size: number;
  }> = [];
  /** 收件人邮箱到客户对象的映射（用于 Chip 上显示客户详情入口） */
  @state() private matchedMap: Map<string, Customer> = new Map();
  /** 是否显示定时发送 UI */
  @state() private showSchedule = false;
  /** 定时发送时间（datetime-local 格式字符串） */
  @state() private scheduleAt = "";
  /** 编辑器内容（同步给 crm-rich-editor 的 value） */
  @state() private _editingContent = "";
  /** 当前选中的邮件模板 ID */
  @state() private selectedTemplateId = 0;
  /** 片段下拉选择器的当前值 */
  @state() private _snippetSelectValue = "";
  /** 片段 ID 到内容的映射缓存（避免重复请求） */
  private _snippetContentMap = new Map<string, string>();
  /** 产品下拉选择器的当前值 */
  @state() private _productSelectValue = '';
  /** 产品 ID 到名称的映射缓存 */
  private _productNameMap = new Map<string, string>();

  /**
   * 防抖通知收件人变化
   *
   * 在收件人/抄送列表变化后延迟 300ms，然后：
   * 1. 调用 fetchMatches() 匹配客户信息（用于 Chip 标记）
   * 2. 派发 `recipients-changed` 事件通知父组件（用于草稿自动保存等）
   *
   * 防抖设计避免快速连续添加/删除收件人时产生过多请求。
   */
  private _scheduleRecipientsChanged() {
    if (this._recipientChangeTimer) clearTimeout(this._recipientChangeTimer);
    this._recipientChangeTimer = window.setTimeout(() => {
      this._recipientChangeTimer = null;
      this.fetchMatches();
      this.dispatchEvent(
        new CustomEvent("recipients-changed", {
          detail: { to: [...this.toRecipients], cc: [...this.ccRecipients] },
          bubbles: true,
          composed: true,
        }),
      );
    }, 300);
  }

  /** display: contents 使组件自身不产生布局盒子，子元素直接参与父级布局 */
  static styles = css`
    :host {
      display: contents;
    }
  `;

  /** emailStore 订阅取消函数（在 disconnectedCallback 中调用） */
  private _unsubscribe?: () => void;

  /**
   * 异步搜索邮件片段（snippet）
   *
   * 调用后端 API 搜索 type=snippet 的模板，同时缓存内容到 _snippetContentMap
   * 以便选择时无需再次请求。
   *
   * @param query - 搜索关键词（空字符串表示不筛选）
   * @returns 选项数组（label=模板名, value=模板 ID 字符串）
   */
  private _asyncSearchSnippets = async (query: string): Promise<{label: string; value: string}[]> => {
    const params: Record<string, any> = { per_page: 20, type: 'snippet' };
    if (query) params.keyword = query;
    const res = await emailTemplateApi.list(params);
    // 缓存片段内容，选中时直接从 Map 取出
    for (const t of (res.data || [])) {
      this._snippetContentMap.set(String(t.id), t.content || '');
    }
    return (res.data || []).map((t: any) => ({ label: t.name, value: String(t.id) }));
  };

  /**
   * 处理片段选择：将片段内容插入到富文本编辑器光标位置
   *
   * @param snippetId - 片段模板 ID
   */
  private handleSnippetSelect = (snippetId: number) => {
    if (!snippetId) return;
    const content = this._snippetContentMap.get(String(snippetId));
    if (!content) return;
    // 获取编辑器实例并调用 insertAtCursor 在光标处插入 HTML
    const editor = this.shadowRoot?.querySelector('crm-rich-editor') as any;
    if (editor?.insertAtCursor) {
      editor.insertAtCursor(content);
    }
    // 重置选择器值，允许再次选择同一片段
    this._snippetSelectValue = '';
    this.requestUpdate();
    setTimeout(() => {
      const el = this.shadowRoot?.querySelector('#snippet-select') as any;
      if (el) el.value = '';
    }, 0);
  }

  /**
   * 异步搜索产品列表
   *
   * @param query - 搜索关键词
   * @returns 选项数组（label=产品名, value=产品 ID 字符串）
   */
  private _asyncSearchProducts = async (query: string): Promise<{label: string; value: string}[]> => {
    const params: Record<string, any> = { per_page: 20 };
    if (query) params.keyword = query;
    const res = await productApi.list(params);
    // 缓存产品名称
    for (const p of (res.data || [])) {
      this._productNameMap.set(String(p.id), p.name);
    }
    return (res.data || []).map((p: any) => ({ label: p.name, value: String(p.id) }));
  };

  /**
   * 处理产品选择：将产品名称插入到富文本编辑器光标位置
   *
   * @param productId - 产品 ID
   */
  private _handleProductSelect = (productId: number) => {
    if (!productId) return;
    const name = this._productNameMap.get(String(productId));
    if (!name) return;
    const editor = this.shadowRoot?.querySelector('crm-rich-editor') as any;
    if (editor?.insertAtCursor) editor.insertAtCursor(name);
    // 重置选择器值
    this._productSelectValue = '';
    this.requestUpdate();
    setTimeout(() => {
      const el = this.shadowRoot?.querySelector('#product-select') as any;
      if (el) el.value = '';
    }, 0);
  }

  /**
   * 异步搜索邮件模板（已发布的，status=1）
   *
   * @param query - 搜索关键词
   * @returns 选项数组（label=模板名, value=模板 ID 字符串）
   */
  private _asyncSearchTemplates = async (query: string): Promise<{label: string; value: string}[]> => {
    const params: Record<string, any> = { per_page: 20, status: 1 };
    if (query) params.keyword = query;
    const res = await emailTemplateApi.list(params);
    return (res.data || []).map((t: any) => ({ label: t.name, value: String(t.id) }));
  };

  /**
   * 处理模板选择：加载模板内容并填充到编辑器和主题输入框
   *
   * 如果模板包含内联图片（CID 引用），将 CID 替换为使用 Cookie 鉴权的后端图片 URL，
   * 确保编辑器中能正确显示图片。
   *
   * @param templateId - 模板 ID（0 表示取消选择）
   * @returns Promise<void>
   */
  private async handleTemplateSelect(templateId: number) {
    this.selectedTemplateId = templateId;
    if (!templateId) {
      this._editingContent = '';
      this.subject = '';
      this.requestUpdate();
      return;
    }
    try {
      const t = await emailTemplateApi.get(templateId);
      // Resolve CID URLs for inline images
      let content = t.content || '';
      if (t.inline_images?.length) {
        for (const img of t.inline_images) {
          if (img.cid) {
            const escaped = img.cid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const url = `${API_BASE_URL}/email-templates/inline-images/${img.id}/file`;
            content = content.replace(new RegExp(`cid:${escaped}`, 'g'), url);
          }
        }
      }
      this._editingContent = content;
      this.subject = t.subject || '';
      // Trigger re-render so subject input updates
      this.requestUpdate();
      // Update subject input directly after render
      await this.updateComplete;
      const subjectInput = this.shadowRoot?.querySelector('input[name="subject"]') as HTMLInputElement;
      if (subjectInput) subjectInput.value = this.subject;
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '加载模板失败');
    }
  }

  /**
   * 组件挂载时触发
   *
   * 1. 订阅 emailStore 获取账户列表更新
   * 2. 如果账户列表为空，触发获取
   * 3. 如果 toEmail 有值（快速回复场景），初始化收件人并触发防抖通知
   */
  connectedCallback() {
    super.connectedCallback();
    // 订阅 emailStore 变化，同步账户列表
    this._unsubscribe = emailStore.subscribe((s) => {
      this.accounts = s.accounts;
    });
    // 首次加载时账户列表可能为空，主动获取
    if (this.accounts.length === 0) {
      emailStore.fetchAccounts();
    }
    // toEmail 用于同步初始化（如快速回复），草稿数据由 willUpdate 异步处理
    if (this.toEmail) {
      this.toRecipients = [this.toEmail];
      this.toEmail = "";
      this._scheduleRecipientsChanged();
    }
  }

  /**
   * Lit updated 生命周期钩子：属性变化后执行
   *
   * 监听多个属性变化：
   * - `composeSession`：变化时重置全部状态并初始化新的会话数据（草稿/转发/回复）
   * - `draftContent`：异步到达的草稿内容，更新编辑器
   * - `draftAttachments`：异步到达的草稿附件，更新附件列表
   * - `toEmail`：快速回复场景的收件人初始化
   *
   * @param changedProps - 变化的属性 Map
   */
  updated(changedProps: Map<string, any>) {
    // composeSession 变化 = 新的写信/转发/回复/编辑草稿会话
    if (changedProps.has('composeSession') && this.composeSession > 0) {
      // 重置所有状态
      this.toRecipients = [];
      this.ccRecipients = [];
      this.toInput = "";
      this.ccInput = "";
      this.toOptions = [];
      this.ccOptions = [];
      this.showToDropdown = false;
      this.showCcDropdown = false;
      this.showCc = false;
      this.showSchedule = false;
      this.scheduleAt = "";
      this.attachedFiles = [];
      this.uploadingAttachments = [];
      this.selectedAccount = null;
      this.selectedTemplateId = 0;
      this._editingContent = '';

      // 使用 microtask 等待所有属性更新完成
      queueMicrotask(() => {
        // 初始化收件人
        if (this.draftTo.length > 0) {
          this.toRecipients = [...this.draftTo];
        }
        if (this.draftCc.length > 0) {
          this.showCc = true;
          this.ccRecipients = [...this.draftCc];
        }

        // 初始化发件账户
        if (this.draftAccountId) {
          this.selectedAccount = this.draftAccountId;
        }

        // 初始化内容
        if (this.draftContent) {
          this._editingContent = this.draftContent;
        }

        // 初始化附件
        if (this.draftAttachments.length > 0) {
          this.attachedFiles = this.draftAttachments.map(a => ({
            id: a.id, filename: a.filename, size: a.size, progress: 100,
          }));
        }

        // 触发客户检索
        const allEmails = [...this.draftTo, ...this.draftCc].filter(Boolean);
        if (allEmails.length > 0) {
          this.fetchMatches();
          if (this._recipientChangeTimer) clearTimeout(this._recipientChangeTimer);
          this._recipientChangeTimer = setTimeout(() => {
            this.dispatchEvent(
              new CustomEvent('recipients-changed', {
                detail: { to: this.draftTo, cc: this.draftCc },
                bubbles: true,
                composed: true,
              }),
            );
          }, 100);
        }
      });
    }

    // draftContent 异步到达（forward/draft 的 API 返回后）
    if (changedProps.has('draftContent') && this.draftContent) {
      this._editingContent = this.draftContent;
    }

    // draftAttachments 异步到达
    if (changedProps.has('draftAttachments') && this.draftAttachments.length > 0) {
      this.attachedFiles = this.draftAttachments.map(a => ({
        id: a.id, filename: a.filename, size: a.size, progress: 100,
      }));
    }

    // toEmail 独立处理（用于快速回复等场景）
    if (changedProps.has('toEmail') && this.toEmail) {
      this.toRecipients = [this.toEmail];
      this.toEmail = "";
      this._scheduleRecipientsChanged();
    }
  }

  /**
   * 组件卸载时触发，清理定时器和订阅防止内存泄漏
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
    if (this.toTimer) clearTimeout(this.toTimer);
    if (this.ccTimer) clearTimeout(this.ccTimer);
    if (this._recipientChangeTimer) clearTimeout(this._recipientChangeTimer);
  }

  // ---- 签名辅助方法 ----
  // 委托给 email-helpers 工具模块，保持组件内代码简洁

  /**
   * 从编辑器内容中移除签名块
   *
   * @param editor - crm-rich-editor 组件实例
   * @returns 不含签名块的 HTML 内容
   */
  private editorContentWithoutSignature(editor: any): string {
    return editorContentWithoutSignature(editor);
  }

  /**
   * 将签名内容包装为带 data-signature-id 的 HTML 块
   *
   * @param sigId - 签名 ID
   * @param sigContent - 签名 HTML 内容
   * @returns 包装后的 HTML 字符串
   */
  private wrapSignatureHTML(sigId: number, sigContent: string): string {
    return wrapSignatureHTML(sigId, sigContent);
  }

  /**
   * 从编辑器中移除已存在的签名块
   *
   * 调用编辑器的 stripSignature 方法，通过 ProseMirror 节点类型查找并删除。
   */
  private removeSignatureFromEditor() {
    const editor = this.shadowRoot!.querySelector("crm-rich-editor") as any;
    editor?.stripSignature?.();
  }

  /**
   * 将签名插入到编辑器中
   *
   * 处理签名中的 CID 图片引用，将其替换为使用 Cookie 鉴权的后端图片 URL。
   * 签名追加在编辑器内容末尾（如果编辑器为空则先填充默认空行）。
   *
   * @param sigId - 签名 ID
   * @param sigContent - 签名 HTML 内容
   */
  private insertSignatureToEditor(sigId: number, sigContent: string) {
    const editor = this.shadowRoot!.querySelector("crm-rich-editor") as any;
    if (!editor || !sigContent) return;
    // 将签名中的 CID 引用（cid:xxx）替换为后端图片 URL
    // 这样编辑器中的内联图片才能正确显示
    const displayContent = sigContent.replace(
      /(src|url)\s*=\s*["']cid:([^"']+)["']/gi,
      (_m: string, attr: string, cid: string) =>
        `${attr}="${API_BASE_URL}/emails/accounts/${this.selectedAccount || 0}/signatures/${sigId}/images/${cid}"`,
    );
    const sigBlock = this.wrapSignatureHTML(sigId, displayContent);
    // 编辑器为空时先填充默认空行，再追加签名
    editor.value = (editor.value || DEFAULT_EMPTY_LINES) + sigBlock;
  }

  /**
   * 加载指定账户的活跃签名并插入到编辑器
   *
   * @param accountId - 发件账户 ID
   * @returns Promise<void>
   */
  private async loadActiveSignature(accountId: number | null) {
    const requestId = ++this._signatureRequestId;
    if (!accountId) return;
    try {
      const sig = await emailStore.fetchActiveSignature(accountId);
      if (requestId === this._signatureRequestId && accountId === this.selectedAccount && sig) {
        this.insertSignatureToEditor(sig.id, sig.content);
      }
    } catch {
      /* silent */
    }
  }

  // ---- 附件事件处理 ----
  // 监听 crm-rich-editor 派发的附件上传事件，更新 UI 状态

  /**
   * 附件上传开始：添加到 uploadingAttachments 列表（显示进度条）
   * @param e - CustomEvent，detail 包含 name 和 size
   */
  private onAttachmentStart(e: Event) {
    const d = (e as CustomEvent).detail;
    this.uploadingAttachments = [...this.uploadingAttachments, d];
  }

  /**
   * 附件上传进度更新：更新对应文件的进度百分比
   * @param e - CustomEvent，detail 包含 name 和 progress
   */
  private onAttachmentProgress(e: Event) {
    const d = (e as CustomEvent).detail;
    this.uploadingAttachments = this.uploadingAttachments.map((a) =>
      a.name === d.name ? { ...a, progress: d.progress } : a,
    );
  }

  /**
   * 附件上传完成：从 uploadingAttachments 移除，添加到 attachedFiles
   * @param e - CustomEvent，detail 包含 id、name、size
   */
  private onAttachmentComplete(e: Event) {
    const d = (e as CustomEvent).detail;
    this.uploadingAttachments = this.uploadingAttachments.filter(
      (a) => a.name !== d.name,
    );
    if (d.id)
      this.attachedFiles = [
        ...this.attachedFiles,
        { id: d.id, filename: d.name, size: d.size },
      ];
  }

  /**
   * 附件上传失败：从 uploadingAttachments 移除并显示错误
   * @param e - CustomEvent，detail 包含 name 和 error
   */
  private onAttachmentError(e: Event) {
    const d = (e as CustomEvent).detail;
    this.uploadingAttachments = this.uploadingAttachments.filter(
      (a) => a.name !== d.name,
    );
    toast.error(d.error || "上传失败");
  }

  /**
   * 从附件列表中移除指定索引的附件
   * @param index - 附件在 attachedFiles 中的索引
   */
  private removeAttachment(index: number) {
    this.attachedFiles = removeAttachmentByIndex(this.attachedFiles, index);
  }

  // ---- 收件人搜索 ----

  /**
   * 收件人/抄送搜索联想
   *
   * 输入少于 3 个字符时清除下拉列表。
   * 300ms 防抖后调用后端 API 搜索匹配的客户邮箱。
   *
   * @param target - 搜索目标：'to'（收件人）或 'cc'（抄送）
   */
  private searchRecipients(target: "to" | "cc") {
    const q = target === "to" ? this.toInput.trim() : this.ccInput.trim();
    if (q.length < 3) {
      this.clearDropdown(target);
      return;
    }
    const timerKey = target === "to" ? "toTimer" : "ccTimer";
    if ((this as any)[timerKey]) clearTimeout((this as any)[timerKey]);
    (this as any)[timerKey] = window.setTimeout(async () => {
      (this as any)[timerKey] = null;
      try {
        const options = await searchRecipientOptions(q);
        if (target === "to") {
          this.toOptions = options;
          this.showToDropdown = options.length > 0;
        } else {
          this.ccOptions = options;
          this.showCcDropdown = options.length > 0;
        }
      } catch {
        this.clearDropdown(target);
      }
    }, 300);
  }

  /**
   * 清除收件人/抄送下拉搜索联想列表
   *
   * @param target - 清除目标：'to' 或 'cc'
   */
  private clearDropdown(target: "to" | "cc") {
    if (target === "to") {
      this.showToDropdown = false;
      this.toOptions = [];
    } else {
      this.showCcDropdown = false;
      this.ccOptions = [];
    }
  }

  /**
   * 添加收件人/抄送邮箱
   *
   * 校验邮箱包含 @ 符号且不重复，然后添加到列表并清空输入。
   * 触发防抖通知父组件收件人变化。
   *
   * @param target - 添加目标：'to' 或 'cc'
   * @param email - 邮箱地址
   */
  private addRecipient(target: "to" | "cc", email: string) {
    if (!email || !email.includes("@")) return;
    const list = target === "to" ? this.toRecipients : this.ccRecipients;
    // 去重：避免同一邮箱添加多次
    if (list.includes(email)) return;
    if (target === "to") {
      this.toRecipients = [...list, email];
      this.toInput = "";
      this.showToDropdown = false;
    } else {
      this.ccRecipients = [...list, email];
      this.ccInput = "";
      this.showCcDropdown = false;
    }
    this._scheduleRecipientsChanged();
  }

  // ---- Chip 输入组件 ----

  /**
   * 匹配收件人邮箱对应的客户信息
   *
   * 将收件人和抄送邮箱列表发送到后端，返回邮箱到客户对象的映射。
   * 匹配成功的邮箱在 Chip 上显示为可点击样式（蓝色），点击打开客户详情。
   *
   * @returns Promise<void>
   */
  private async fetchMatches() {
    this.matchedMap = await fetchRecipientMatches(this.toRecipients, this.ccRecipients);
  }

  /**
   * 处理 Chip 点击事件
   *
   * 如果该邮箱匹配到客户，打开客户详情弹窗。
   *
   * @param email - 被点击的邮箱地址
   */
  private handleChipClick(email: string) {
    handleRecipientChipClick(email, this.matchedMap, this.shadowRoot);
  }

  /**
   * 渲染 Chip 式收件人/抄送输入组件
   *
   * 功能包括：
   * - 已添加邮箱的 Chip 显示（可点击删除）
   * - 邮箱搜索联想下拉列表
   * - 输入框（Enter 添加、Backspace 删除最后一个）
   * - 匹配客户的 Chip 可点击打开详情
   *
   * @param target - 渲染目标：'to'（收件人）或 'cc'（抄送）
   * @returns Lit 模板
   */
  private renderChipInput(target: "to" | "cc") {
    const recipients = target === "to" ? this.toRecipients : this.ccRecipients;
    const inputValue = target === "to" ? this.toInput : this.ccInput;
    const options = target === "to" ? this.toOptions : this.ccOptions;
    const showDropdown =
      target === "to" ? this.showToDropdown : this.showCcDropdown;

    return html`
      <div
        class="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-border bg-surface rounded text-sm transition-all focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 min-h-[42px] relative"
      >
        ${recipients.map(
          (email) => {
            const isMatched = this.matchedMap.has(email.toLowerCase());
            return html`
            <span
              class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium shrink-0 ${isMatched ? 'text-info border border-info/40 bg-info/5 cursor-pointer hover:underline' : 'text-text-muted border border-border bg-bg'}"
              @click=${() => { if (isMatched) this.handleChipClick(email); }}
            >
              ${email}
              <button
                type="button"
                class="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-primary/10 cursor-pointer text-primary/60 hover:text-primary transition-colors"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  if (target === "to")
                    this.toRecipients = recipients.filter((r) => r !== email);
                  else
                    this.ccRecipients = this.ccRecipients.filter(
                      (r) => r !== email,
                    );
                  this._scheduleRecipientsChanged();
                }}
              >
                ×
              </button>
            </span>
          `;
          },
        )}
        <input
          type="text"
          class="chip-input-${target} flex-1 min-w-[120px] px-1 py-1 border-none outline-none bg-transparent text-sm text-text placeholder:text-text-muted"
          placeholder="${target === "to"
            ? "输入邮箱地址后按 Enter 添加..."
            : "输入抄送邮箱..."}"
          .value=${inputValue}
          @input=${(e: Event) => {
            const val = (e.target as HTMLInputElement).value;
            if (target === "to") this.toInput = val;
            else this.ccInput = val;
            this.searchRecipients(target);
          }}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") {
              e.preventDefault();
              this.addRecipient(target, inputValue);
            }
            if (e.key === "Backspace" && !inputValue && recipients.length > 0) {
              if (target === "to") this.toRecipients = recipients.slice(0, -1);
              else this.ccRecipients = this.ccRecipients.slice(0, -1);
              this._scheduleRecipientsChanged();
            }
          }}
          @focus=${() => {
            if (target === "to")
              this.showToDropdown = this.toOptions.length > 0;
            else this.showCcDropdown = this.ccOptions.length > 0;
          }}
          @blur=${() => setTimeout(() => this.clearDropdown(target), 200)}
        />
        ${showDropdown
          ? html`
              <div
                class="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-md shadow-lg z-20 max-h-40 overflow-y-auto"
              >
                ${options.map(
                  (opt) => html`
                    <div
                      class="px-3 py-1.5 text-sm text-text hover:bg-bg cursor-pointer"
                      @mousedown=${(e: Event) => {
                        e.preventDefault();
                        this.addRecipient(target, opt.value);
                      }}
                    >
                      ${opt.label}
                    </div>
                  `,
                )}
              </div>
            `
          : ""}
      </div>
    `;
  }

  // ---- 发送 ----

  /**
   * 处理邮件发送表单提交
   *
   * 执行以下步骤：
   * 1. 校验收件人和发件账户
   * 2. 获取编辑器内容（去除签名块）
   * 3. 如果是转发邮件，拼接转发头和引用内容
   * 4. 校验主题非空
   * 5. 调用后端发送 API
   * 6. 成功后派发 `crm-email-compose-sent` 事件
   *
   * @param e - 表单提交事件
   * @returns Promise<void>
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    // 校验收件人：至少需要一个收件人
    if (this.toRecipients.length === 0) {
      toast.error("请添加至少一个收件人");
      return;
    }
    // 校验发件账户
    if (!this.selectedAccount) {
      toast.error("请选择发送账户");
      return;
    }
    const form = e.target as HTMLFormElement;
    const editor = this.shadowRoot!.querySelector("crm-rich-editor") as any;
    // 获取编辑器内容并去除签名块（签名不作为邮件正文发送）
    const editorContent = this.editorContentWithoutSignature(editor);
    // 转发邮件：在正文后拼接转发头和引用的原始邮件内容
    const content = this.forwardHtml
      ? editorContent + sanitizeEmailHtml(this.forwardHeader) + cleanQuoteHtml(this.forwardHtml)
      : editorContent;
    // 从 DOM 直接获取主题输入值（确保拿到最新值）
    const subjectVal = (
      form.querySelector('input[name="subject"]') as HTMLInputElement
    )?.value?.trim();
    if (!subjectVal) {
      toast.error("请输入邮件主题");
      return;
    }

    try {
      await emailSendApi.send({
        account_id: this.selectedAccount,
        to_email: this.toRecipients,
        cc: this.ccRecipients,
        subject: subjectVal,
        content,
        content_type: "html",
        customer_id: this.customerId || 0,
        attachment_ids: this.attachedFiles.map((f) => f.id),
        inline_image_ids: [],
        in_reply_to: this.replyToMessageId,
        references: this.replyReferences,
        thread_id: this.replyThreadId,
        // 定时发送：将 datetime-local 字符串转为 Unix 时间戳（秒）
        schedule_at: this.scheduleAt
          ? Math.floor(new Date(this.scheduleAt).getTime() / 1000)
          : undefined,
      });
      toast.success("邮件发送成功");
      // 通知父组件邮件已发送（父组件可关闭撰写区域等）
      this.dispatchEvent(
        new CustomEvent("crm-email-compose-sent", {
          bubbles: true,
          composed: true,
        }),
      );
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || "发送失败");
    }
  }

  /**
   * 关闭撰写组件：派发 `crm-email-compose-close` 事件
   */
  private close() {
    this.dispatchEvent(new CustomEvent('crm-email-compose-close', { bubbles: true, composed: true }));
  }

  /**
   * 保存邮件草稿
   *
   * 收集当前表单数据（账户、收件人、主题、正文、附件）并调用后端草稿 API。
   * 成功后显示提示并关闭撰写组件。
   *
   * @param e - 触发事件（可选）
   * @returns Promise<void>
   */
  private async handleSaveDraft(e?: Event) {
    e?.preventDefault();
    const form = this.shadowRoot!.querySelector("form") as HTMLFormElement;
    const editor = this.shadowRoot!.querySelector("crm-rich-editor") as any;
    const content = this.editorContentWithoutSignature(editor);
    const subjectVal = (form.querySelector('input[name="subject"]') as HTMLInputElement)?.value?.trim() || "";

    try {
      await emailSendApi.saveDraft({
        account_id: this.selectedAccount || 0,
        to_email: this.toRecipients,
        cc: this.ccRecipients,
        subject: subjectVal,
        content,
        content_type: 'html',
        customer_id: this.customerId || 0,
        attachment_ids: this.attachedFiles.map(f => f.id),
        inline_image_ids: [],
        in_reply_to: this.replyToMessageId,
        references: this.replyReferences,
        thread_id: this.replyThreadId,
        draft_id: this.draftId || undefined,
      });
      toast.success("草稿已保存");
      this.close();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || "保存失败");
    }
  }

  /**
   * 渲染邮件撰写表单
   *
   * 表单结构：
   * 1. 发件账户选择（crm-select，仅显示启用状态的账户）
   * 2. 收件人 Chip 输入（renderChipInput('to')）
   * 3. 抄送区域（可折叠，点击"添加抄送"展开）
   * 4. 主题输入框
   * 5. 附件区域（上传中进度 + 已完成列表）
   * 6. 富文本编辑器（crm-rich-editor），内嵌签名选择器、片段/产品/模板下拉
   * 7. 转发引用内容区域（如有 forwardHtml）
   * 8. 底部操作栏：定时发送 / 保存草稿 / 取消 / 发送
   *
   * 监听的附件事件：
   * - crm-attachment-start / progress / complete / error
   *
   * @returns Lit 模板
   */
  render() {
    return html`
      <form
        @submit=${this.handleSubmit}
        class="flex flex-col h-full gap-4"
        @crm-attachment-start=${this.onAttachmentStart}
        @crm-attachment-progress=${this.onAttachmentProgress}
        @crm-attachment-complete=${this.onAttachmentComplete}
        @crm-attachment-error=${this.onAttachmentError}
      >
        <div>
          <label class="block mb-1.5 text-sm font-medium text-text"
            >邮箱账户 *</label
          >
          <crm-select
            fullWidth
              .options=${this.accounts.length === 0
                ? [{ label: "无可用账户", value: "" }]
                : [
                    { label: "选择账户", value: "" },
                    ...this.accounts.filter((a) => a.status === 1).map((a) => ({
                    label: a.address,
                    value: String(a.id),
                  })),
                ]}
            .value=${this.selectedAccount !== null
              ? String(this.selectedAccount)
              : ""}
            .disabled=${this.accounts.length === 0}
            @change=${(e: CustomEvent) => {
              this.selectedAccount = e.detail.value
                ? parseInt(e.detail.value)
                : null;
              this.loadActiveSignature(this.selectedAccount);
            }}
          ></crm-select>
        </div>
        <div>
          <label class="block mb-1.5 text-sm font-medium text-text"
            >收件人 *</label
          >
          ${this.renderChipInput("to")}
        </div>
        <div>
          ${this.showCc
            ? html`
                <label class="block mb-1.5 text-sm font-medium text-text"
                  >抄送</label
                >
                ${this.renderChipInput("cc")}
              `
            : html`
                <button
                  type="button"
                  class="text-sm text-primary hover:text-primary-hover cursor-pointer bg-transparent border-none p-0"
                  @click=${() => {
                    this.showCc = true;
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14" class="inline-block mr-0.5 align-text-bottom"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>添加抄送
                </button>
              `}
        </div>
        <div>
          <label class="block mb-1.5 text-sm font-medium text-text"
            >主题 *</label
          >
          <input
            type="text"
            name="subject"
            required
            .value=${this.subject}
            class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
          />
        </div>
        <div class="flex-1 min-h-0 flex flex-col">
          <label class="mb-1.5 text-sm font-medium text-text">内容 *</label>
          ${this.uploadingAttachments.length > 0 ||
          this.attachedFiles.length > 0
            ? html` <div class="flex flex-wrap gap-2 mb-2">
                ${this.uploadingAttachments.map(
                  (a) =>
                    html` <div
                      class="flex items-center gap-1.5 px-2.5 py-1 bg-bg rounded-md border border-border text-xs"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        class="w-3 h-3 text-text-muted shrink-0"
                      >
                        <path
                          d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                        />
                      </svg>
                      <span class="text-text truncate max-w-[120px]"
                        >${a.name}</span
                      >
                      <span class="text-text-muted shrink-0"
                        >${formatFileSize(a.size)}</span
                      >
                      <div
                        class="w-12 h-1 bg-border rounded-full overflow-hidden shrink-0"
                      >
                        <div
                          class="h-full bg-primary rounded-full transition-all duration-200"
                          style="width:${a.progress}%"
                        ></div>
                      </div>
                    </div>`,
                )}
                ${this.attachedFiles.map(
                  (f, i) =>
                    html` <div
                      class="flex items-center gap-1.5 px-2.5 py-1 bg-info/5 rounded-md border border-info/20 text-xs"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        class="w-3 h-3 text-info shrink-0"
                      >
                        <path
                          d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                        />
                      </svg>
                      <span class="text-text truncate max-w-[120px]"
                        >${f.filename}</span
                      >
                      <span class="text-text-muted shrink-0"
                        >${formatFileSize(f.size)}</span
                      >
                      <button
                        type="button"
                        class="p-0.5 rounded hover:bg-bg cursor-pointer text-text-muted hover:text-error transition-colors shrink-0 leading-none"
                        @click=${() => this.removeAttachment(i)}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          class="w-3 h-3"
                        >
                          <path d="M18 6 6 18" />
                          <path d="m6 6 12 12" />
                        </svg>
                      </button>
                    </div>`,
                )}
              </div>`
            : ""}
          <div class="flex-1 min-h-0 overflow-y-auto">
          <crm-rich-editor
            name="content"
            placeholder="输入邮件内容..."
            required
            style="min-height:200px;flex:auto;"
            .noScroll=${this.forwardHtml ? true : false}
            .value=${this._editingContent}
          >
            <crm-signature-selector
              slot="toolbar-end"
              .accountId=${this.selectedAccount || 0}
              @crm-signature-changed=${(e: CustomEvent) => {
                if (e.detail.content)
                  this.insertSignatureToEditor(e.detail.id, e.detail.content);
              }}
              @crm-signature-removed=${() => this.removeSignatureFromEditor()}
            ></crm-signature-selector>
            <crm-select slot="toolbar-extra" id="snippet-select"
              borderless
              fitContent
              small
              .asyncSearch=${this._asyncSearchSnippets}
              placeholder="插入片段"
              .value=${this._snippetSelectValue}
              @change=${(e: CustomEvent) => this.handleSnippetSelect(Number(e.detail.value))}
            ></crm-select>
            <crm-select slot="toolbar-extra" id="product-select"
              borderless
              fitContent
              small
              .asyncSearch=${this._asyncSearchProducts}
              placeholder="插入产品"
              .value=${this._productSelectValue}
              @change=${(e: CustomEvent) => this._handleProductSelect(Number(e.detail.value))}
            ></crm-select>
            <crm-select slot="toolbar-extra" id="template-select"
              borderless
              fitContent
              small
              clearable
              .asyncSearch=${this._asyncSearchTemplates}
              placeholder="使用模板"
              .value=${String(this.selectedTemplateId || '')}
              @change=${(e: CustomEvent) => this.handleTemplateSelect(Number(e.detail.value))}
            ></crm-select>
          </crm-rich-editor>
          ${this.forwardHtml ? html`
            <div class="border-t border-border px-4 py-2 text-sm shrink-0" style="color:#60646c;background:#fafafa;">
              ${unsafeHTML(sanitizeEmailHtml(this.forwardHeader))}
              ${unsafeHTML(cleanQuoteHtml(this.forwardHtml))}
            </div>
          ` : ""}
          </div>
        </div>
        <div
          class="flex justify-end gap-2 mt-auto pt-4 border-t border-border shrink-0 items-center flex-wrap"
        >
          ${this.showSchedule
            ? html`
                <input
                  type="datetime-local"
                  .value=${this.scheduleAt}
                  @input=${(e: Event) => {
                    this.scheduleAt = (e.target as HTMLInputElement).value;
                  }}
                  class="px-3 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-1 focus:ring-primary/20 focus:outline-none"
                />
                <button
                  type="button"
                  class="text-xs text-text-muted hover:text-text cursor-pointer bg-transparent border-none"
                  @click=${() => {
                    this.showSchedule = false;
                    this.scheduleAt = "";
                  }}
                >
                  取消定时
                </button>
              `
            : html`
                <button
                  type="button"
                  class="text-xs text-text-muted hover:text-text cursor-pointer bg-transparent border-none px-2"
                  @click=${() => {
                    this.showSchedule = true;
                  }}
                >
                  定时发送
                </button>
              `}
          <div class="flex-1"></div>
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-medium text-text-secondary border border-border bg-surface hover:bg-bg rounded transition-colors cursor-pointer"
            @click=${this.handleSaveDraft}
          >
            保存草稿
          </button>
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer"
            @click=${this.close}
          >
            取消
          </button>
          <button
            type="submit"
            class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20"
          >
            ${this.showSchedule ? "定时发送" : "发送"}
          </button>
        </div>
      </form>
      <crm-customer-view-modal id="customerViewModal"></crm-customer-view-modal>
    `;
  }
}
